import Reflux from 'reflux';
import { Map } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import UserSessionStore from 'flux/stores/UserSessionStore';
import ServerTimeStore from 'flux/stores/ServerTimeStore';
import CuesheetCueClockActions from 'flux/actions/cuesheet/CuesheetCueClockActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import _ from 'underscore';
import moment from 'moment-timezone';
import KeyBindingActions from 'flux/actions/KeyBindingActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';

/**
 * Cuesheet Cue Clock Store
 *
 */
export default Reflux.createStore({
  listenables: [CuesheetCueClockActions, RealTimeActions],

  mixins: [BaseMixin],

  init() {
    this.setState(
      Map({
        toggleFullScreenClock: false,
      }),
    );

    this.listenTo(ServerTimeStore, 'onServerTimeSync');
    this.listenTo(UserSessionStore, 'onUserSession');
  },

  onUserSession(user) {
    this._userId = user.get('_id');
  },

  _getUserId() {
    return this._userId;
  },

  populate(trackingUserId, trackingEventId, trackingShowId, clockState) {
    let toggleFullScreenClock = this.getInState('toggleFullScreenClock');

    this._trackingUserId = trackingUserId;
    this._trackingEventId = trackingEventId;
    this._trackingShowId = trackingShowId;

    this.setState(clockState || Map({}));

    // used for the populate function to retain the value
    this.setInState('toggleFullScreenClock', toggleFullScreenClock);

    if (this.getInState('ticking')) {
      //set up interval
      this._startTicking(false);
    } else {
      this._triggerClockState();
    }
  },

  onPlayCueClockWithDuration(userId, eventId, showId, durationInSeconds) {
    this._trackingUserId = userId;
    this._trackingEventId = eventId;
    this._trackingShowId = showId;

    this._stopTicking(false);

    this.setState(
      this.getState()
        .set('startTime', this._getCurrentUtc().valueOf())
        .set('durationInSeconds', durationInSeconds),
    );

    this._startTicking(true);
  },

  onToggleFullScreenClock() {
    let showClock = this.getInState('toggleFullScreenClock') ? false : true;

    if (showClock) {
      KeyBindingActions.unbindHotKeys();
      KeyBindingActions.unbindNavigationKeys();
    } else {
      KeyBindingActions.bindHotKeys();
      KeyBindingActions.bindNavigationKeys();
    }

    this.setInStateAndTrigger('toggleFullScreenClock', showClock);
  },

  onServerTimeSync(date) {
    this._serverDate = date;
  },

  onCueClockMutatedRealTime(userId, showId, payload) {
    if (userId !== this._trackingUserId || showId !== this._trackingShowId) {
      return;
    }

    let mutationRaw = _.omit(payload, 'eventId');

    let next = this.getState().merge(mutationRaw);

    this.setState(next);

    this._stopTicking(false);

    if (next.get('ticking')) {
      this._startTicking(false);
    }
  },

  // eslint-disable-next-line no-unused-vars
  onUserRemovedTrackingRealTime(params, payload) {
    if (params.userId !== this._trackingUserId) return;
    this._zeroClockAndStop();
  },

  // if updateDuration is true, override the current duration with actual cue clock duration
  onUpdateItemDurationsToCurrentDuration(
    rowIndex,
    updateDuration,
    updateActualDuration,
  ) {
    let clockState = this._getClockState();
    let elapsed =
      clockState.get('durationInSeconds') - clockState.get('reading');

    if (updateActualDuration) {
      CuesheetCellActions.saveItemTimeFields(
        rowIndex,
        Map({ actualDurationInSeconds: elapsed }),
      );
    }
    if (updateDuration) {
      CuesheetCellActions.saveItemTimeFields(
        rowIndex,
        Map({ durationInSeconds: elapsed }),
      );
    }
  },

  onClearClock() {
    delete this._trackingUserId;
    delete this._trackingShowId;
    delete this._trackingEventId;

    this._zeroClockAndStop();
  },

  _zeroClockAndStop() {
    this._clearInterval();

    this.setState(
      this.getState().set('durationInSeconds', 0).set('ticking', false),
    );

    this._triggerClockState();
  },

  _stopTicking(persist) {
    let clockState = this._getClockState();

    this.setInState('ticking', false);
    this.setInState('durationInSeconds', clockState.get('reading'));

    if (persist) this._persist();

    this._triggerClockState();

    this._clearInterval();
  },

  _startTicking(persist) {
    this.setInState('ticking', true);

    if (persist) this._persist();

    this._triggerClockState();

    this._startInterval();
  },

  _persist() {
    CuesheetCueClockActions.updateCueClockPersistent(
      this._getUserId(),
      this._trackingEventId,
      this._trackingShowId,
      this.getInState('startTime'),
      this.getInState('ticking'),
      this.getInState('durationInSeconds'),
    );
  },

  _startInterval() {
    this._clearInterval();

    this._interval = setInterval(() => {
      this._triggerClockState();

      this._serverDate += 1000;
    }, 1000);
  },

  _triggerClockState() {
    let state = this._getClockState();
    //console.log("Clock tick (%s): %s", state.get('reading'));
    this.trigger(state);
  },

  _clearInterval() {
    if (!this._interval) return;
    clearInterval(this._interval);
  },

  _getCurrentUtc() {
    const serverTime = this._serverDate;

    return serverTime ? moment.utc(serverTime) : moment.utc();
  },

  _getClockState() {
    const state = this.getState();

    const currentUtc = this._getCurrentUtc(),
      startTimeUtc = moment.utc(state.get('startTime'));

    let elapsed = currentUtc.diff(startTimeUtc, 'seconds'),
      duration = state.get('durationInSeconds'),
      clockReading = duration;

    if (state.get('ticking')) {
      clockReading = duration - elapsed;
    }

    return state.set('reading', clockReading);
  },
});
